/**
 * The button over an estimate picture that saves the one shown (Admin →
 * Settings → Estimate output, app setting `downloadButton`): a download
 * icon in a corner of the picture, at the admin's size, colour and opacity.
 * The file is named after the project and the view (watermark.ts
 * downloadFileName). Over the 360 there is none.
 */

import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { downloadFileName, type DownloadButtonSettings } from './watermark';

export interface DownloadButtonProps {
  settings: DownloadButtonSettings;
  /** The picture shown; nothing to save without one. */
  url: string | null;
  project: string;
  view: string;
  /** The preview in the admin: looks the part, saves nothing. */
  inert?: boolean;
}

/** Where the button sits: a corner of the picture, clear of the summary strip at the bottom. */
export function downloadButtonCss(s: DownloadButtonSettings): React.CSSProperties {
  const inset = 12;
  return {
    position: 'absolute',
    zIndex: 6,
    width: s.size,
    height: s.size,
    color: s.color,
    opacity: s.opacity,
    ...(s.position.startsWith('top') ? { top: inset } : { bottom: `calc(var(--rvc-overlay-h, 0px) + ${inset}px)` }),
    ...(s.position.endsWith('left') ? { left: inset } : { right: inset }),
  };
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({ settings, url, project, view, inert }) => {
  const { t } = useTranslation('results');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!settings.enabled || !url) return null;
  const save = async () => {
    if (inert || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = downloadFileName(project, view, ext);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const label = failed ? t('render.downloadFailed') : busy ? t('render.downloading') : t('render.download');
  return (
    <button type="button" className="rvc-download" style={downloadButtonCss(settings)} onClick={save} aria-label={label} title={label} disabled={busy}>
      {busy ? <Loader2 size={Math.round(settings.size * 0.55)} className="rvc-download-spin" /> : <Download size={Math.round(settings.size * 0.55)} />}
    </button>
  );
};

export default DownloadButton;
